import React, { useState, useEffect } from 'react';
import { Sparkles, Crown, Users, Calendar, X, ChevronRight, Zap, Heart, Plane, Trophy } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const UpcomingFeaturesBanner = () => {
  // Use localStorage to persist dismissal across sessions
  const [dismissed, setDismissed] = useState(() => {
    return localStorage.getItem('honeydo_banner_dismissed') === 'true';
  });
  const [showDetails, setShowDetails] = useState(false);
  const [pricingView, setPricingView] = useState('subscriptions'); // 'subscriptions' or 'bundles'

  const handleDismiss = () => {
    setDismissed(true);
    localStorage.setItem('honeydo_banner_dismissed', 'true');
  };

  const features = [
    {
      icon: <Calendar className="w-5 h-5" />,
      title: 'Event Tiers',
      description: 'Organize large projects with hierarchical events and tasks',
      tag: 'Coming Soon',
      color: 'from-purple-500 to-indigo-500'
    },
    {
      icon: <Crown className="w-5 h-5" />,
      title: 'Premium Plans',
      description: 'Unlock unlimited events, advanced collaboration, and priority support',
      tag: 'Early Access',
      color: 'from-amber-500 to-orange-500'
    },
    {
      icon: <Users className="w-5 h-5" />,
      title: 'Relationship Types',
      description: 'Specialized workflows for roommates, landlords, builders, and more',
      tag: 'Beta',
      color: 'from-blue-500 to-cyan-500'
    }
  ];

  const subscriptionTiers = [
    {
      name: 'Free',
      price: '$0',
      features: ['Nuclear Family support', '3 active events', 'Up to 5 family members', 'Basic task management'],
      highlight: false
    },
    {
      name: 'Premium',
      price: '$9.99',
      period: '/month',
      features: ['Unlimited events', 'Unlimited members', 'Advanced relationship types', 'Priority support', 'Contractor referrals'],
      highlight: true
    },
    {
      name: 'Enterprise',
      price: '$29.99',
      period: '/month',
      features: ['Everything in Premium', 'Builder & Buyer workflows', 'Performance Groups', 'Custom branding', 'Dedicated support'],
      highlight: false
    }
  ];

  const eventBundles = [
    {
      name: 'Wedding Planning',
      icon: <Heart className="w-6 h-6" />,
      price: '$99',
      period: '/year',
      features: ['Unlimited guests/vendors', '1 year access', 'Wedding-specific templates', 'Vendor coordination tools', 'Guest RSVP management', 'Budget tracking'],
      color: 'from-pink-500 to-rose-500',
      highlight: false
    },
    {
      name: 'Group Trip',
      icon: <Plane className="w-6 h-6" />,
      price: '$49',
      period: '/6 months',
      features: ['Up to 20 travelers', '6 month access', 'Itinerary planning', 'Expense splitting', 'Travel checklist templates', 'Shared packing lists'],
      color: 'from-blue-500 to-cyan-500',
      highlight: true
    },
    {
      name: 'Service Club',
      icon: <Trophy className="w-6 h-6" />,
      price: '$149',
      period: '/year',
      features: ['Unlimited members', '1 year renewable', 'Event management', 'Volunteer coordination', 'Project tracking', 'Community engagement tools'],
      color: 'from-amber-500 to-orange-500',
      highlight: false
    }
  ];

  return (
    <>
      {/* Show compact button if banner was dismissed */}
      {dismissed && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <button
            onClick={() => setShowDetails(true)}
            className="w-full px-4 py-3 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 text-white font-semibold rounded-lg hover:shadow-lg transition-all transform hover:scale-[1.02] flex items-center justify-center gap-2"
          >
            <Sparkles className="w-5 h-5" />
            View Premium Plans & Event Packages
            <ChevronRight className="w-5 h-5" />
          </button>
        </motion.div>
      )}

      {/* Show full banner if NOT dismissed */}
      {!dismissed && (
        <AnimatePresence>
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="relative overflow-hidden rounded-xl bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 p-[2px] mb-6"
          >
        <div className="relative bg-white dark:bg-gray-900 rounded-xl p-6">
          {/* Close button */}
          <button
            onClick={handleDismiss}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Header */}
          <div className="flex items-start gap-3 mb-4">
            <div className="p-2 bg-gradient-to-br from-purple-500 to-indigo-500 rounded-lg">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-1">
                Exciting Features Coming to HoneyDo!
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                We're building powerful new collaboration tools. Here's what's next:
              </p>
            </div>
          </div>

          {/* Feature Cards */}
          <div className="grid md:grid-cols-3 gap-4 mb-4">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.1 }}
                className="relative p-4 bg-gray-50 dark:bg-gray-800 rounded-lg hover:shadow-md transition-shadow group cursor-pointer"
                onClick={() => setShowDetails(true)}
              >
                <div className={`absolute top-2 right-2 px-2 py-1 bg-gradient-to-r ${feature.color} text-white text-xs font-semibold rounded-full`}>
                  {feature.tag}
                </div>
                <div className={`mb-3 p-2 bg-gradient-to-r ${feature.color} rounded-lg inline-flex text-white`}>
                  {feature.icon}
                </div>
                <h4 className="font-semibold text-gray-900 dark:text-white mb-1">
                  {feature.title}
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {feature.description}
                </p>
                <div className="mt-2 text-sm text-indigo-600 dark:text-indigo-400 font-medium group-hover:translate-x-1 transition-transform inline-flex items-center gap-1">
                  Learn more <ChevronRight className="w-4 h-4" />
                </div>
              </motion.div>
            ))}
          </div>

          {/* CTA */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
              <Zap className="w-4 h-4 text-amber-500" />
              <span>Be among the first to get early access!</span>
            </div>
            <button
              onClick={() => setShowDetails(true)}
              className="px-6 py-2 bg-gradient-to-r from-indigo-500 to-purple-500 text-white font-semibold rounded-lg hover:shadow-lg transition-all transform hover:scale-105"
            >
              View Pricing Plans
            </button>
          </div>
        </div>
      </motion.div>
        </AnimatePresence>
      )}

      {/* Details Modal - Always render so button works after dismissal */}
      <AnimatePresence>
        {showDetails && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowDetails(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white dark:bg-gray-900 rounded-2xl p-8 max-w-5xl w-full max-h-[90vh] overflow-y-auto"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-3xl font-bold text-gray-900 dark:text-white">
                  Choose Your Plan
                </h2>
                <button
                  onClick={() => setShowDetails(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Pricing View Tabs */}
              <div className="flex gap-2 mb-6 bg-gray-100 dark:bg-gray-800 p-1 rounded-lg">
                <button
                  onClick={() => setPricingView('subscriptions')}
                  className={`flex-1 px-4 py-2 rounded-md font-semibold transition-all ${
                    pricingView === 'subscriptions'
                      ? 'bg-white dark:bg-gray-700 text-purple-600 dark:text-purple-400 shadow-md'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                  }`}
                >
                  Monthly Subscriptions
                </button>
                <button
                  onClick={() => setPricingView('bundles')}
                  className={`flex-1 px-4 py-2 rounded-md font-semibold transition-all ${
                    pricingView === 'bundles'
                      ? 'bg-white dark:bg-gray-700 text-purple-600 dark:text-purple-400 shadow-md'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                  }`}
                >
                  Event Bundles
                </button>
              </div>

              <p className="text-gray-600 dark:text-gray-400 mb-8 text-center">
                {pricingView === 'subscriptions'
                  ? 'Start free and upgrade anytime. All plans include core task management features.'
                  : 'Special one-time packages for specific life events. Perfect for temporary collaboration needs.'
                }
              </p>

              <div className="grid md:grid-cols-3 gap-6">
                {(pricingView === 'subscriptions' ? subscriptionTiers : eventBundles).map((tier, index) => (
                  <div
                    key={index}
                    className={`relative p-6 rounded-xl border-2 ${
                      tier.highlight
                        ? 'border-purple-500 shadow-xl scale-105'
                        : 'border-gray-200 dark:border-gray-700'
                    }`}
                  >
                    {tier.highlight && (
                      <div className={`absolute -top-3 left-1/2 transform -translate-x-1/2 px-4 py-1 ${
                        tier.color ? `bg-gradient-to-r ${tier.color}` : 'bg-gradient-to-r from-purple-500 to-indigo-500'
                      } text-white text-sm font-bold rounded-full`}>
                        {pricingView === 'bundles' ? 'Most Popular' : 'Most Popular'}
                      </div>
                    )}
                    <div className="text-center mb-6">
                      {tier.icon && (
                        <div className={`inline-flex p-3 rounded-xl bg-gradient-to-br ${tier.color} text-white mb-3`}>
                          {tier.icon}
                        </div>
                      )}
                      <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                        {tier.name}
                      </h3>
                      <div className="flex items-baseline justify-center gap-1">
                        <span className="text-4xl font-bold text-gray-900 dark:text-white">
                          {tier.price}
                        </span>
                        {tier.period && (
                          <span className="text-gray-600 dark:text-gray-400">
                            {tier.period}
                          </span>
                        )}
                      </div>
                    </div>
                    <ul className="space-y-3 mb-6">
                      {tier.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-400">
                          <div className="w-5 h-5 rounded-full bg-green-100 dark:bg-green-900 flex items-center justify-center mt-0.5">
                            <svg className="w-3 h-3 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          </div>
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <button
                      className={`w-full py-3 rounded-lg font-semibold transition-all ${
                        tier.highlight
                          ? 'bg-gradient-to-r from-purple-500 to-indigo-500 text-white hover:shadow-lg'
                          : 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white hover:bg-gray-200 dark:hover:bg-gray-700'
                      }`}
                      disabled
                    >
                      Coming Soon
                    </button>
                  </div>
                ))}
              </div>

              <div className="mt-8 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <p className="text-sm text-center text-blue-900 dark:text-blue-300">
                  <strong>Early Access:</strong> Sign up for our waitlist to get notified when premium features launch
                  and receive exclusive beta access!
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default UpcomingFeaturesBanner;
